import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { useAuth } from "@/lib/auth";

export default function ResetPassword() {
  const { resetPassword, api } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";

  const [tokenValid, setTokenValid] = useState(null); // null=loading, true, false
  const [maskedEmail, setMaskedEmail] = useState("");
  const [tokenError, setTokenError] = useState("");

  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setTokenValid(false); setTokenError("Missing reset token."); return; }
    api.get(`/auth/reset-password/verify`, { params: { token } })
      .then(({ data }) => {
        setTokenValid(!!data.valid);
        setMaskedEmail(data.email || "");
        if (!data.valid) setTokenError(data.reason || "Invalid or expired link.");
      })
      .catch(() => { setTokenValid(false); setTokenError("Could not verify the reset link."); });
  }, [token, api]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not reset password.";
      toast.error(detail);
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <AuthShell title="Checking your link…">
        <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </AuthShell>
    );
  }

  if (tokenValid === false) {
    return (
      <AuthShell
        title="This link is no longer valid"
        subtitle={tokenError}
        footer={<Link to="/forgot-password" className="font-bold" style={{ color: "var(--brand)" }}>Request a new link</Link>}
      >
        <div />
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="All set!" subtitle="You can now sign in with your new password.">
        <div className="flex flex-col items-center gap-4 py-2" data-testid="reset-done">
          <CheckCircle2 className="w-14 h-14" style={{ color: "var(--brand)" }} />
          <button onClick={() => navigate("/login")} className="btn btn-green" data-testid="reset-goto-login">
            Go to sign in
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle={maskedEmail ? `Resetting password for ${maskedEmail}` : ""}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="reset-form">
        <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          New password
          <input
            type="password" required minLength={8} autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-2 px-4 py-3 rounded-xl border text-base font-normal normal-case tracking-normal"
            style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
            data-testid="reset-password"
          />
        </label>
        <button type="submit" disabled={loading} className="btn btn-green mt-2 justify-center w-full" data-testid="reset-submit">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save new password"}
        </button>
      </form>
    </AuthShell>
  );
}
